import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { errorMessage, formatDateTime } from "helpers/common";
import { api } from "helpers/axios";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import ListPagination from "components/ui/ListPagination";

import ErrorAlert from "components/ui/ErrorAlert";

import UserImportWizard from "./UserImportWizard";
import { buildLoginUserImportDetailPath, IMPORT_TABS } from "./importNavigation";
import { POLLABLE_STATUSES } from "./importHelpers";
import { useTranslation } from "translation/useTranslation";
import TableScrollbar from "components/EmployeeManagement/TableScrollbar";
import { Button } from "components/ui";

export default function UserImport({ tabsComponent, counter }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [imports, setImports] = useState(null);
  const [mode, setMode] = useState('view');

  const { editableTranslate } = useTranslation();

  const [pagination, setPagination] = useState({
    totalRows: 0,
    totalPages: 1,
  });

  const tableRef = useRef(null);
  const theadRef = useRef(null);

  const [resizedCount, setResizedCount] = useState(0);

  const currentPage = Math.max(1, Number(searchParams.get("page") || 1));
  const currentPerPage = [20, 50, 100].includes(Number(searchParams.get("per_page"))) ? Number(searchParams.get("per_page")) : 20;

  const fetchUserImports = useCallback(async ({ silent = false, background = false, page = null } = {}) => {
    if (!page) page = currentPage;

    try {
      if (!silent) setLoading(true);
      const { data, status } = await api.get(`/user-imports?limit=${currentPerPage}&page=${page}`, { meta: { background } });
      if (status === 200) {
        setImports(data.data);
        const temp = data.meta.pagination;
        setPagination({ totalRows: +temp.total, totalPages: temp.total_pages });
      }
    } catch (err) {
      setError(errorMessage(err, 'fetchUserImports'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [currentPage, currentPerPage]);

  const shouldPoll = useMemo(() => {
    return mode === 'view' && Array.isArray(imports) && imports.some((item) => POLLABLE_STATUSES.includes(item.status));
  }, [mode, imports]);

  useEffect(() => {
    if (mode === 'view') {
      fetchUserImports();
    }
  }, [mode, fetchUserImports]);

  useEffect(() => {
    setMode('view');
  }, [counter]);

  useEffect(() => {
    if (!shouldPoll) return;

    const intervalId = setInterval(() => {
      fetchUserImports({ silent: true, background: true });
    }, 5000);

    return () => clearInterval(intervalId);
  }, [shouldPoll, fetchUserImports]);

  useEffect(() => {
    setResizedCount(c => c + 1);

    const theadEl = theadRef.current;
    if (!theadEl) return;

    const handleScrollTable = () => {
      const navHeight = document.getElementById('navbar-menu')?.offsetHeight || 0;

      const theadParentCoordinates = theadEl.parentElement.getBoundingClientRect();
      if (theadParentCoordinates.y < navHeight) {
        theadEl.style.transform = `translate3d(0, ${-theadParentCoordinates.y + navHeight}px, 0)`;
      } else {
        theadEl.style.transform = 'translate3d(0,0,0)';
      }
    };
    window.addEventListener('scroll', handleScrollTable);
    return () => window.removeEventListener('scroll', handleScrollTable);
  }, [imports]);

  const handleImportClick = (item) => {
    navigate(buildLoginUserImportDetailPath(item?.hash_id ?? item?.data?.hash_id, location.search || `?tab=${IMPORT_TABS.loginUsers}`));
  };

  const handleChangePage = (page) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("page", String(page));
    nextSearchParams.set("per_page", String(currentPerPage));
    nextSearchParams.set("tab", IMPORT_TABS.loginUsers);
    setSearchParams(nextSearchParams);
  };

  const handleChangePerPage = (perPage) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("page", "1");
    nextSearchParams.set("per_page", String(perPage));
    nextSearchParams.set("tab", IMPORT_TABS.loginUsers);
    setSearchParams(nextSearchParams);
  };

  return (
    <>
      <div className="col-lg col-md-9 section-col-right d-flex">
        <div className="bg-white w-100 p-4 content-card">
          <div className="d-flex justify-content-between align-items-center border-bottom employee-management-tabs-div mb-4">
            {tabsComponent}

            <div className="position-relative">
              <div className="d-flex">
                <Button variant="secondary" className="shadow-none ml-2" onClick={() => setMode('add')} data-testid="new-import-button">
                  <i className="bi bi-plus"></i> {editableTranslate('user_import.new_import', 'Neuer Import')}
                </Button>
              </div>
              <div className="employee-filters-separator"></div>
            </div>
          </div>

          {error && (
            <div className="mt-3">
              <ErrorAlert error={error} onClose={() => setError(null)} />
            </div>
          )}

          {mode === 'add' && (
            <UserImportWizard setMode={setMode} handleImportClick={handleImportClick} />
          )}

          {mode === 'view' && (
            <div className="mt-2">
              <div className="mt-3">
                <div className="table-responsive has-custom-scrollbar">
                  <table ref={tableRef} className="table common-table" data-testid="imports-table">
                    <thead ref={theadRef} className="sticky-top">
                      <tr>
                        <th scope="col">{editableTranslate('user_import.file_name', 'Dateiname')}</th>
                        <th scope="col">{editableTranslate('user_import.total_records', 'Anzahl der Datensätze')}</th>
                        <th scope="col">{editableTranslate('user_import.imported_records', 'Importierte Datensätze')}</th>
                        <th scope="col">{editableTranslate('user_import.status', 'Status')}</th>
                        <th scope="col">{editableTranslate('user_import.created_at', 'Datum')}</th>
                        <th scope="col" className="text-center">{editableTranslate('user_import.action', 'Aktion')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!loading && Array.isArray(imports) && imports.length > 0 && imports.map((item, index) => (
                        <tr data-testid={`import-row-${item.hash_id}`}
                          key={index}
                          className="cursor-pointer table-row"
                          onClick={() => handleImportClick(item)}
                        >
                          <td>{item.original_file_name}</td>
                          <td>{item.total_records}</td>
                          <td>{item.imported_records}</td>
                          <td>
                            <div>
                              <span className={`badge ${['completed', 'partially_completed'].includes(item.status) ? 'badge-success' : item.status === 'failed' ? 'badge-danger' : 'badge-warning'}`}>
                                {editableTranslate('user_import.status.' + item.status, item.status_formatted)}
                              </span>
                              {['scheduled', 'importing'].includes(item.status) && (() => {
                                const progressValue = Math.min(Math.max(Math.round(item.progress), 0), 100);
                                return (
                                  <div className="mt-2">
                                    <div className="progress" style={{ height: '6px' }}>
                                      <div
                                        className="progress-bar"
                                        role="progressbar"
                                        style={{ width: `${progressValue}%` }}
                                        aria-valuenow={progressValue}
                                        aria-valuemin="0"
                                        aria-valuemax="100"
                                      ></div>
                                    </div>
                                    <small className="text-muted d-block mt-1">{progressValue}%</small>
                                  </div>
                                );
                              })()}
                            </div>
                          </td>
                          <td>{formatDateTime(item.date_formatted?.created_at)}</td>
                          <td className="text-center v-middle">
                            <span className="btn btn-sm btn-outline-primary shadow-none text-nowrap">
                              {editableTranslate('user_import.details', 'Details anzeigen')}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {(loading || !imports) && (
                        <tr>
                          <td colSpan={7}>
                            <div className="d-flex justify-content-center" data-testid="loading">
                              <div className="spinner-border" role="status"><span className="sr-only">Wird geladen...</span></div>
                            </div>
                          </td>
                        </tr>
                      )}
                      {!loading && Array.isArray(imports) && imports.length === 0 && (
                        <tr>
                          <td colSpan={7} className="text-center" data-testid="empty-state">{editableTranslate('global.no_data', 'Keine Einträge')}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <TableScrollbar tableRef={tableRef} data={imports} trigger={resizedCount} />

                <ListPagination
                  page={currentPage}
                  perPage={currentPerPage}
                  totalPages={pagination.totalPages}
                  totalRows={pagination.totalRows}
                  setPage={handleChangePage}
                  setPerPage={handleChangePerPage}
                  className="mt-4"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
